import { useState, useEffect } from "react";
import axios from "axios";
import { BookOpen, Calendar, Tag, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "", label: "All", color: "#FFFFFF" },
  { id: "cybertruck", label: "Cybertruck", color: "#E31937" },
  { id: "starlink", label: "Starlink", color: "#00BFFF" },
  { id: "tesla", label: "Tesla", color: "#22C55E" },
];

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [category, setCategory] = useState("");
  const [selectedPost, setSelectedPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const url = category ? `${API}/blog/posts?category=${category}` : `${API}/blog/posts`;
        const res = await axios.get(url);
        setPosts(res.data);
      } catch (err) {
        console.error("Load posts error:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [category]);

  if (selectedPost) {
    return (
      <div data-testid="blog-post-detail" className="min-h-screen pt-20 pb-12 px-6 md:px-12" style={{ background: '#0A0A0C' }}>
        <div className="max-w-3xl mx-auto">
          <button
            onClick={() => setSelectedPost(null)}
            data-testid="back-to-blog"
            className="flex items-center gap-1 text-sm text-slate-400 hover:text-white mb-8 transition-colors"
          >
            <ChevronRight className="w-4 h-4 rotate-180" /> Back to articles
          </button>
          {selectedPost.image_url && (
            <div className="w-full h-64 mb-8 overflow-hidden border border-[#2A2A30]">
              <img src={selectedPost.image_url} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs tracking-[0.15em] uppercase font-semibold px-3 py-1 border border-[#2A2A30]"
              style={{ color: CATEGORIES.find((c) => c.id === selectedPost.category)?.color || '#FFF' }}>
              {selectedPost.category}
            </span>
            <span className="text-xs text-slate-500">{selectedPost.author}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {selectedPost.title}
          </h1>
          <div className="prose prose-invert max-w-none text-slate-300 leading-relaxed whitespace-pre-wrap">
            {selectedPost.content}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="blog-page" className="min-h-screen pt-20 pb-12 px-6 md:px-12" style={{ background: '#0A0A0C' }}>
      <div className="max-w-7xl mx-auto">
        <div className="mb-10">
          <span className="text-xs tracking-[0.2em] uppercase text-[#00BFFF] font-semibold">CONTENT HUB</span>
          <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Cybertruck, Tesla & Starlink
          </h1>
          <p className="text-base text-slate-400 mt-3">Latest articles, guides, and insights for the Tesla community.</p>
        </div>

        {/* Category Filter */}
        <div className="flex gap-2 mb-10 overflow-x-auto">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setCategory(cat.id)}
              data-testid={`blog-filter-${cat.label.toLowerCase()}`}
              className={`px-5 py-2 text-xs font-bold uppercase tracking-wider transition-colors whitespace-nowrap ${
                category === cat.id
                  ? "bg-[#E31937] text-white"
                  : "bg-[#141417] border border-[#2A2A30] text-slate-400 hover:text-white hover:border-white/30"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#E31937] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : posts.length === 0 ? (
          <div className="bg-[#141417] border border-[#2A2A30] p-12 text-center">
            <BookOpen className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>No Articles Yet</h3>
            <p className="text-sm text-slate-400">Check back soon for new content.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border border-[#2A2A30]">
            {posts.map((post, i) => (
              <motion.button
                key={post.id}
                onClick={() => setSelectedPost(post)}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                data-testid={`blog-post-${post.id}`}
                className="text-left bg-[#141417] border border-[#2A2A30] overflow-hidden group hover:bg-[#1C1C20] transition-all duration-300"
              >
                {post.image_url && (
                  <div className="w-full h-48 overflow-hidden">
                    <img src={post.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Tag className="w-3 h-3" style={{ color: CATEGORIES.find((c) => c.id === post.category)?.color || '#FFF' }} />
                    <span className="text-xs uppercase tracking-wider text-slate-500">{post.category}</span>
                  </div>
                  <h3 className="text-base font-bold text-white mb-2 group-hover:text-[#E31937] transition-colors" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {post.title}
                  </h3>
                  <p className="text-sm text-slate-400 line-clamp-2">{post.summary || post.content?.slice(0, 120) + "..."}</p>
                  <div className="flex items-center gap-1 mt-4 text-xs text-[#00BFFF] font-semibold uppercase">
                    Read More <ChevronRight className="w-3 h-3" />
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
